import React, { useState, useMemo } from "react";
import { Room, RoomSortBy, RoomDisplayFormat } from "../services/types";
import { useTranslation } from "../i18n";
import "./Directory.css";

interface DirectoryProps {
  rooms: Room[];
  onClose: () => void;
  onCallRoom: (room: Room, userIds?: string[]) => void;
  callingRoomId?: string | null;
  roomSortBy?: RoomSortBy;
  roomDisplayFormat?: RoomDisplayFormat;
}

const Directory: React.FC<DirectoryProps> = ({
  rooms,
  onClose,
  onCallRoom,
  callingRoomId,
  roomSortBy = "roomName",
  roomDisplayFormat = "roomName",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRooms, setExpandedRooms] = useState<Set<string>>(new Set());
  const { t, translate } = useTranslation();

  // Filter and sort rooms based on search term and sortBy
  const filteredAndSortedRooms = useMemo(() => {
    let filtered = rooms;

    // Filter rooms based on search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = rooms.filter((room) => {
        const roomNameMatch = room.name.toLowerCase().includes(searchLower);
        const displayNameMatch = room.displayName
          ?.toLowerCase()
          .includes(searchLower);
        const floorMatch = room.floorName?.toLowerCase().includes(searchLower);
        const tenantFirstMatch = room.tenantFirstName
          ?.toLowerCase()
          .includes(searchLower);
        const tenantLastMatch = room.tenantLastName
          ?.toLowerCase()
          .includes(searchLower);

        // Also search in tenants array
        const tenantsMatch = room.tenants?.some(
          (tenant) =>
            tenant.firstName.toLowerCase().includes(searchLower) ||
            tenant.lastName.toLowerCase().includes(searchLower)
        );

        return (
          roomNameMatch ||
          displayNameMatch ||
          floorMatch ||
          tenantFirstMatch ||
          tenantLastMatch ||
          tenantsMatch
        );
      });
    }

    // Sort rooms based on roomSortBy
    const sorted = [...filtered].sort((a, b) => {
      switch (roomSortBy) {
        case "firstName":
          // Prefer tenants array, fallback to tenantFirstName
          const aTenants = a.tenants || [];
          const bTenants = b.tenants || [];
          const aFirstName =
            aTenants.length > 0
              ? aTenants[0].firstName
              : a.tenantFirstName || "";
          const bFirstName =
            bTenants.length > 0
              ? bTenants[0].firstName
              : b.tenantFirstName || "";
          return aFirstName.localeCompare(bFirstName);
        case "lastName":
          // Prefer tenants array, fallback to tenantLastName
          const aTenantsLast = a.tenants || [];
          const bTenantsLast = b.tenants || [];
          const aLastName =
            aTenantsLast.length > 0
              ? aTenantsLast[0].lastName
              : a.tenantLastName || "";
          const bLastName =
            bTenantsLast.length > 0
              ? bTenantsLast[0].lastName
              : b.tenantLastName || "";
          return aLastName.localeCompare(bLastName);
        case "floorName":
          const aFloor = a.floorName || "";
          const bFloor = b.floorName || "";
          if (aFloor !== bFloor) {
            return aFloor.localeCompare(bFloor);
          }
          // If same floor, sort by room name
          return (a.displayName || a.name).localeCompare(
            b.displayName || b.name
          );
        case "roomName":
        default:
          return (a.displayName || a.name).localeCompare(
            b.displayName || b.name
          );
      }
    });

    return sorted;
  }, [rooms, searchTerm, roomSortBy]);

  const toggleRoomExpand = (roomId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRooms((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(roomId)) {
        newSet.delete(roomId);
      } else {
        newSet.add(roomId);
      }
      return newSet;
    });
  };

  // Format room display name based on roomDisplayFormat
  const formatRoomDisplay = (
    room: Room,
    showAllTenants: boolean = false
  ): string => {
    const roomName = room.displayName || room.name;
    const floorName = room.floorName;

    // Get tenants to display - prefer tenants array, fallback to tenantFirstName/tenantLastName
    const tenants = room.tenants || [];
    let displayTenants: Array<{ firstName: string; lastName: string }> = [];

    if (tenants.length > 0) {
      displayTenants = showAllTenants ? tenants : tenants.slice(0, 1); // Show first tenant by default
    } else if (room.tenantFirstName || room.tenantLastName) {
      // Fallback to tenantFirstName/tenantLastName if tenants array is empty
      displayTenants = [
        {
          firstName: room.tenantFirstName || "",
          lastName: room.tenantLastName || "",
        },
      ];
    }

    const formatTenantName = (tenant: {
      firstName: string;
      lastName: string;
    }) => {
      return `${tenant.firstName} ${tenant.lastName}`.trim();
    };

    switch (roomDisplayFormat) {
      case "tenantName":
        if (displayTenants.length > 0) {
          const names = displayTenants.map(formatTenantName).join(", ");
          return names || roomName;
        }
        return roomName;

      case "both":
        if (displayTenants.length > 0) {
          const names = displayTenants.map(formatTenantName).join(", ");
          return names ? `${roomName} - ${names}` : roomName;
        }
        return roomName;

      case "roomNameWithFloor":
        return floorName ? `${floorName} - ${roomName}` : roomName;

      case "tenantNameWithFloor":
        if (displayTenants.length > 0) {
          const names = displayTenants.map(formatTenantName).join(", ");
          if (names) {
            return floorName ? `${floorName} - ${names}` : names;
          }
        }
        return floorName ? `${floorName} - ${roomName}` : roomName;

      case "bothWithFloor":
        if (displayTenants.length > 0) {
          const names = displayTenants.map(formatTenantName).join(", ");
          if (names) {
            return floorName
              ? `${floorName} - ${roomName} - ${names}`
              : `${roomName} - ${names}`;
          }
        }
        return floorName ? `${floorName} - ${roomName}` : roomName;

      case "roomName":
      default:
        return roomName;
    }
  };

  const handleRoomClick = (room: Room) => {
    if (!callingRoomId) {
      // Only allow clicking if no room is currently being called
      // Call all tenants in the room (no userIds specified)
      onCallRoom(room);
    }
  };

  const handleResidentClick = (
    e: React.MouseEvent,
    room: Room,
    tenantUserId?: string
  ) => {
    e.stopPropagation();
    if (!callingRoomId) {
      if (tenantUserId) {
        // Call only the specific resident
        onCallRoom(room, [tenantUserId]);
      } else {
        // If no userId available, fallback to calling all tenants in the room
        onCallRoom(room);
      }
    }
  };

  // Check if we should use nested structure (room + residents)
  const useNestedStructure =
    roomDisplayFormat === "both" ||
    roomDisplayFormat === "bothWithFloor" ||
    roomDisplayFormat === "bothLastFirst";

  return (
    <div className="directory-overlay">
      <div className="directory-modal">
        {/* 头部 */}
        <div className="directory-header">
          <h2 className="directory-title">{t.directory.title}</h2>
          <button
            className="directory-close-btn"
            onClick={onClose}
            disabled={!!callingRoomId} // Disable close button when calling
          >
            ✕
          </button>
        </div>

        {/* 搜索框 */}
        <div className="directory-search">
          <input
            type="text"
            placeholder={t.directory.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="directory-search-input"
            disabled={!!callingRoomId} // Disable search when calling
          />
        </div>

        {/* 房间列表 */}
        <div className="directory-content">
          {filteredAndSortedRooms.length === 0 ? (
            <div className="directory-empty">
              <p>{t.directory.noRooms}</p>
            </div>
          ) : (
            <div className="room-list">
              {filteredAndSortedRooms.map((room) => {
                const isCallingThisRoom = callingRoomId === room.id;
                const tenants = room.tenants || [];

                // Get tenants to display - prefer tenants array, fallback to tenantFirstName/tenantLastName
                let displayTenants: Array<{
                  firstName: string;
                  lastName: string;
                }> = [];
                if (tenants.length > 0) {
                  displayTenants = tenants;
                } else if (room.tenantFirstName || room.tenantLastName) {
                  displayTenants = [
                    {
                      firstName: room.tenantFirstName || "",
                      lastName: room.tenantLastName || "",
                    },
                  ];
                }

                // If using nested structure (both format), show room + residents separately
                if (useNestedStructure) {
                  // Only show room name/number, no tenant names in the room row
                  // Use room.name (room number) to ensure no tenant info is included
                  const roomNumber = room.name;
                  const floorName = room.floorName;
                  // For bothWithFloor, always show floor if available
                  const roomDisplayName =
                    roomDisplayFormat === "bothWithFloor" && floorName
                      ? `${floorName} - ${roomNumber}`
                      : roomNumber;
                  const isExpanded = expandedRooms.has(room.id);
                  const hasResidents = displayTenants.length > 0;

                  return (
                    <div key={room.id} className="room-group">
                      {/* Room row */}
                      <div
                        className={`room-item room-item-header ${
                          isCallingThisRoom ? "calling" : ""
                        }`}
                      >
                        <div className="room-info">
                          <div className="room-name">{roomDisplayName}</div>
                          {hasResidents && (
                            <button
                              className="room-expand-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRoomExpand(room.id, e);
                              }}
                              disabled={!!callingRoomId}
                            >
                              {isExpanded ? "▼" : "▶"}{" "}
                              {isExpanded
                                ? "Hide residents"
                                : `Show residents (${displayTenants.length})`}
                            </button>
                          )}
                        </div>
                        <div
                          className="room-call-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRoomClick(room);
                          }}
                        >
                          {isCallingThisRoom ? (
                            <span className="call-icon calling">⏳</span>
                          ) : (
                            <span className="call-icon">📞</span>
                          )}
                        </div>
                      </div>

                      {/* Resident rows - only show if expanded and there are tenants */}
                      {isExpanded &&
                        hasResidents &&
                        tenants.map((tenant, index) => {
                          const tenantName =
                            roomDisplayFormat === "bothLastFirst"
                              ? `${tenant.lastName}, ${tenant.firstName}`.trim()
                              : `${tenant.firstName} ${tenant.lastName}`.trim();
                          return (
                            <div
                              key={`${room.id}-tenant-${
                                tenant.userId || index
                              }`}
                              className="room-item room-item-resident"
                              onClick={(e) =>
                                handleResidentClick(e, room, tenant.userId)
                              }
                            >
                              <div className="room-info">
                                <div className="room-name resident-name">
                                  {tenantName}
                                </div>
                              </div>
                              <div className="room-call-btn resident-call-btn">
                                <span className="call-icon">📞</span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  );
                }

                // Default flat structure for other formats
                const isExpanded = expandedRooms.has(room.id);
                const hasMultipleTenants = tenants.length > 1;
                const shouldShowExpand =
                  (roomDisplayFormat === "tenantName" ||
                    roomDisplayFormat === "tenantNameWithFloor") &&
                  hasMultipleTenants;

                return (
                  <div
                    key={room.id}
                    className={`room-item ${
                      isCallingThisRoom ? "calling" : ""
                    }`}
                    onClick={() => handleRoomClick(room)}
                  >
                    <div className="room-info">
                      <div className="room-name">
                        {formatRoomDisplay(room, isExpanded)}
                      </div>
                      {shouldShowExpand && (
                        <button
                          className="room-expand-btn"
                          onClick={(e) => toggleRoomExpand(room.id, e)}
                          disabled={!!callingRoomId}
                        >
                          {isExpanded ? "▼" : "▶"}{" "}
                          {isExpanded
                            ? "Show less"
                            : `Show all (${tenants.length})`}
                        </button>
                      )}
                    </div>
                    <div className="room-call-btn">
                      {isCallingThisRoom ? (
                        <span className="call-icon calling">⏳</span>
                      ) : (
                        <span className="call-icon">📞</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 底部信息 */}
        <div className="directory-footer">
          <p className="directory-count">
            {translate(t.directory.roomsCount, {
              count: filteredAndSortedRooms.length,
              total: rooms.length,
            })}
            {callingRoomId && (
              <span className="calling-status"> - {t.directory.calling}</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Directory;
